import { z } from "zod";

/**
 * When each answer was said in the session recording, down to the word.
 *
 * The server transcribes the recording's audio with word timestamps after the call and matches
 * those words to each scored answer's text (see the server's practice-interview/timing). Every
 * time is in seconds on the recording's own timeline, so it can be given straight to the
 * video element's `currentTime`.
 */

export const practiceAnswerTimingSchema = z.object({
  /** When the answer starts and ends in the recording. */
  start: z.number().min(0),
  end: z.number().min(0),
  /**
   * One entry per word of the answer text: [character offset where the word starts, start, end].
   * Words the transcription missed (it drops some "um"s) are placed between their neighbours.
   */
  words: z.array(z.tuple([z.number().int().min(0), z.number().min(0), z.number().min(0)])),
  /** Filler words and phrases found in the answer text, with when each was said. */
  fillers: z.array(
    z.object({ word: z.string(), char: z.number().int().min(0), at: z.number().min(0) }),
  ),
  /** Share of the answer's words found in the recording, 0-1: lower means rougher times. */
  matched: z.number().min(0).max(1),
});
export type PracticeAnswerTiming = z.infer<typeof practiceAnswerTimingSchema>;

export const PRACTICE_TIMING_STATUSES = ["pending", "ready", "failed", "unavailable"] as const;
export type PracticeTimingStatus = (typeof PRACTICE_TIMING_STATUSES)[number];

export const practiceSessionTimingSchema = z.object({
  /**
   * pending: being worked out (or the recording isn't uploaded yet); ready: `answers` is filled;
   * failed: it went wrong and will be retried on a later visit; unavailable: there is no
   * recording to time against.
   */
  status: z.enum(PRACTICE_TIMING_STATUSES),
  /** By answer key (see practiceAnswerKey). An answer that couldn't be placed is left out. */
  answers: z.record(z.string(), practiceAnswerTimingSchema),
});
export type PracticeSessionTiming = z.infer<typeof practiceSessionTimingSchema>;

/** The key an answer's timing is stored under: its question and attempt. */
export const practiceAnswerKey = (answer: { questionIndex: number; attempt: number }) =>
  `${answer.questionIndex}:${answer.attempt}`;

/** Index of the last word starting at or before `char` (0 when `char` is before every word). */
const wordAt = (timing: PracticeAnswerTiming, char: number) => {
  const { words } = timing;
  let lo = 0;
  let hi = words.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (words[mid][0] <= char) lo = mid;
    else hi = mid - 1;
  }
  return lo;
};

/** When the text at character `char` of the answer was said. */
export const practiceTimeAt = (timing: PracticeAnswerTiming, char: number): number =>
  timing.words.length ? timing.words[wordAt(timing, char)][1] : timing.start;

/** When the characters [from, to) of the answer were said, start to end. */
export const practiceSpanTime = (
  timing: PracticeAnswerTiming,
  from: number,
  to: number,
): { start: number; end: number } => {
  if (!timing.words.length) return { start: timing.start, end: timing.end };
  const first = timing.words[wordAt(timing, from)];
  const last = timing.words[wordAt(timing, Math.max(from, to - 1))];
  return { start: first[1], end: Math.max(first[2], last[2]) };
};

/** A recording time as m:ss (or h:mm:ss past the hour). */
export const formatPracticeClock = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};
